import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Crown, DollarSign, Users, CalendarClock } from 'lucide-react';
import AdminLayout from '../../components/layout/AdminLayout';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (d) => (d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const daysTo = (d) => (d ? Math.round((new Date(d + 'T12:00:00') - new Date()) / 86400000) : null);

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#94A3B8]"><Icon size={14} />{label}</div>
    <div className="text-3xl font-bold text-[#0A0F1C] mt-2">{value}</div>
  </div>
);

const StatusPill = ({ m }) => {
  const [txt, cls] = m.status !== 'active' ? ['Canceled', 'bg-slate-100 text-slate-500']
    : m.cancel_at_period_end ? ['Canceling', 'bg-orange-100 text-orange-700'] : ['Active', 'bg-emerald-100 text-emerald-700'];
  return <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cls}`}>{txt}</span>;
};

export default function AdminMemberships() {
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}/api/admin/memberships`, { withCredentials: true }).then((r) => setData(r.data)).catch(() => setData({ members: [], by_tier: [], mrr: 0, active_count: 0, canceling: 0 })); }, []);
  const renewals = (data?.members || []).filter((r) => r.membership.status === 'active' && !r.membership.cancel_at_period_end && daysTo(r.membership.next_billing_date) !== null && daysTo(r.membership.next_billing_date) <= 7);
  return (
    <AdminLayout title="Memberships">
      <div data-testid="admin-memberships-page" className="max-w-6xl">
        <h1 className="text-3xl font-bold text-[#0A0F1C] mb-1">Memberships</h1>
        <p className="text-sm text-[#64748B] mb-6">Monthly tool memberships: who's active, what they pay, and who renews next.</p>
        {!data ? <Loader2 className="animate-spin text-[#1D4ED8]" /> : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Stat icon={DollarSign} label="Monthly revenue" value={`$${data.mrr.toLocaleString()}`} testid="memberships-mrr" />
              <Stat icon={Users} label="Active members" value={data.active_count} testid="memberships-active" />
              <Stat icon={CalendarClock} label="Renewing in 7 days" value={renewals.length} testid="memberships-renewing" />
              <Stat icon={Crown} label="Canceling" value={data.canceling} testid="memberships-canceling" />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="memberships-by-tier">
              {data.by_tier.map((t) => (
                <div key={t.name} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-4 py-3 text-sm flex justify-between">
                  <span className="font-semibold text-[#0A0F1C]">{t.name} <span className="font-normal text-[#94A3B8]">${t.price}/mo</span></span>
                  <span className="font-bold text-[#1D4ED8]">{t.count}</span>
                </div>
              ))}
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-x-auto">
              <table className="w-full text-sm" data-testid="memberships-table">
                <thead className="bg-[#F8FAFC] text-left text-xs uppercase tracking-wider text-[#94A3B8]">
                  <tr><th className="px-4 py-3">Member</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Tools</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Next billing</th></tr>
                </thead>
                <tbody>
                  {data.members.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-[#94A3B8]" data-testid="memberships-empty">No memberships yet. They'll appear here as soon as someone subscribes.</td></tr>}
                  {data.members.map((r) => (
                    <tr key={r.client_id || r.email} className="border-t border-[#F1F5F9]" data-testid={`membership-row-${r.client_id}`}>
                      <td className="px-4 py-3"><Link to={`/admin/clients/${r.client_id}`} className="font-semibold text-[#0A0F1C] hover:text-[#1D4ED8]">{r.name || r.email}</Link><div className="text-xs text-[#94A3B8]">{r.email}</div></td>
                      <td className="px-4 py-3">{r.membership.name} <span className="text-[#94A3B8]">${r.membership.price}/mo</span></td>
                      <td className="px-4 py-3 text-xs text-[#64748B]">{(r.membership.tools || []).length} tools</td>
                      <td className="px-4 py-3"><StatusPill m={r.membership} /></td>
                      <td className="px-4 py-3">{fmt(r.membership.next_billing_date)}{daysTo(r.membership.next_billing_date) !== null && daysTo(r.membership.next_billing_date) <= 7 && r.membership.status === 'active' && <span className="ml-2 text-xs font-bold text-[#F97316]">soon</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
